import { appInsights } from "../AppInsights";

export enum LogLevel {
  INFO = "INFO",
  WARNING = "WARNING",
  ERROR = "ERROR",
  CRITICAL = "CRITICAL",
}

export class TelemetryLogger {
  private static _instance: TelemetryLogger;

  private constructor() {

  }

  /**
   * Returns the singleton
   * */
  public static get instance() {
    if (!TelemetryLogger._instance) {
      TelemetryLogger._instance = new TelemetryLogger();
    }
    return TelemetryLogger._instance;
  }

  /**
   * A needed generic payload for each call
   * */
  private enrichPayload() {
    return {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: appInsights.context.getSessionId(),
    }
  }

  /**
   * Used to log user actions
   * */
  public async logUserAction(act: string) {
    console.log("[logUserAction] act: ", act);

    appInsights.trackEvent({
      name: act,
      properties: {
        level: LogLevel.INFO,
        ...this.enrichPayload(),
      }
    });
  }

  /**
   * Will log all result of API call - whether it was done or not
   *
   * The reason this method is async is to ensure that the telemetry is sent
   * before the response is returned to the caller.
   *
   * @param res - The response object from the API call
   * @param url - The URL of the API endpoint
   * @param req - The request payload, if any
   * */
  public async logRestApiCall(res: Response, url: string, req?: any) {
    console.log("[logRestApiCall] url: ", url, " res: ", res);
    try {
      if (res.ok) {
        appInsights.trackEvent({
          name: `${url}`,
          properties: {
            status: res.status,
            statusText: res.statusText,
            success: true,
            payload: req ? JSON.stringify(req) : undefined,
          },
        });
      } else {
        appInsights.trackException({
          exception: new Error(`Rest API call failed: ${res.status} ${res.statusText}`),
          severityLevel: 3,
          properties: {
            url,
            status: res.status,
            statusText: res.statusText,
            success: false,
            payload: req ? JSON.stringify(req) : undefined,
          },
        });
      }
    } catch (e) {
      console.error("[logRestApiCall] Error logging API call: ", e);
      appInsights.trackException({
        exception: e as Error,
        severityLevel: 3,
        properties: {
          url,
          status: res.status,
          statusText: res.statusText,
          success: false,
          payload: req ? JSON.stringify(req) : undefined,
        },
      });
    }
  }

  /**
   * Logs exceptions with a severity level
   * @param error - The error to log
   * @param level - The severity level of the error
   * */
  public async logException(error: Error, level = LogLevel.ERROR) {
    console.error("[logException] error: ", error);

    appInsights.trackException({
      exception: error,
      severityLevel: level === LogLevel.CRITICAL ? 4 : 3, // 4 for critical, 3 for error
      properties: {
        level,
        ...this.enrichPayload(),
        errorMessage: error.message,
      },
    });
  }
}
